import { Trash2 } from 'lucide-react';
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
  cn,
} from '@/components/ui';
import { hasResidual } from '@/logic/weights';
import { formatAzimuth, formatFixed } from '@/logic/statistics';
import { isCoordinate } from '@/utils/fileParser';
import type { PointPair, SystemPoint, TransformationSolution } from '@/types';

interface PointPairDetailsProps {
  pair: PointPair;
  system1Points: SystemPoint[];
  system2Points: SystemPoint[];
  onUpdatePair: (
    id: number,
    field: keyof PointPair,
    value: string | number,
    autofill?: boolean
  ) => void;
  onRemovePair: (id: number) => void;
  onClose: () => void;
  solution?: TransformationSolution | null;
  trialSolution?: TransformationSolution | null;
  solutionStale?: boolean;
  /** Project units label; omitted when unspecified. */
  units?: string | null;
}

const SIDES = [
  {
    side: 'Record',
    identifier: 'sys1Point',
    north: 'n1',
    east: 'e1',
    description: 'desc1',
    tone: 'text-record',
  },
  {
    side: 'Field',
    identifier: 'sys2Point',
    north: 'n2',
    east: 'e2',
    description: 'desc2',
    tone: 'text-field',
  },
] as const;

/** The table and map open the same correspondence and coordinate editor. */
export const PointPairDetails = ({
  pair,
  system1Points,
  system2Points,
  onUpdatePair,
  onRemovePair,
  onClose,
  solution,
  trialSolution,
  solutionStale = false,
  units,
}: PointPairDetailsProps) => {
  const fits = [
    { name: solutionStale ? 'Previous inputs' : 'Current fit', fit: solution },
    { name: 'Trial preview', fit: trialSolution },
  ].filter(
    (entry): entry is { name: string; fit: TransformationSolution } =>
      !!entry.fit && !entry.fit.error
  );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        size="lg"
        className="min-w-0"
        title={`Pair ${pair.label}`}
        description="Choose an imported point to fill its coordinates, or edit the identifier and coordinates directly. Changes apply immediately; solve again to update the fit."
        closeLabel="Close pair details"
      >
        <DialogBody className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            {SIDES.map(
              ({ side, identifier, north, east, description, tone }) => {
                const points =
                  side === 'Record' ? system1Points : system2Points;
                const imported = points.some(
                  (point) => point.point === pair[identifier]
                );
                return (
                  <fieldset
                    key={side}
                    className="min-w-0 space-y-3 rounded-lg border border-border bg-surface p-3"
                  >
                    <legend
                      className={cn(
                        'px-1 text-[11px] font-medium uppercase tracking-wide',
                        tone
                      )}
                    >
                      {side}
                    </legend>
                    <Field label={`Imported ${side} point`}>
                      <Select
                        aria-label={`${side} point for pair ${pair.label}`}
                        value={imported ? pair[identifier] : ''}
                        onChange={(event) => {
                          if (event.target.value)
                            onUpdatePair(
                              pair.id,
                              identifier,
                              event.target.value
                            );
                        }}
                      >
                        <option value="">Choose a point…</option>
                        {points.map((point) => (
                          <option key={point.point} value={point.point}>
                            {point.point}
                            {point.description ? ` — ${point.description}` : ''}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Identifier">
                      <Input
                        mono
                        aria-label={`${side} identifier for pair ${pair.label}`}
                        value={pair[identifier]}
                        onChange={(event) =>
                          onUpdatePair(
                            pair.id,
                            identifier,
                            event.target.value,
                            false
                          )
                        }
                      />
                    </Field>
                    <div className="grid gap-3">
                      {(
                        [
                          { name: 'northing', label: 'Northing', field: north },
                          { name: 'easting', label: 'Easting', field: east },
                        ] as const
                      ).map(({ name, label, field }) => {
                        const valid = isCoordinate(pair[field]);
                        return (
                          <Field
                            key={field}
                            label={label}
                            error={
                              valid
                                ? undefined
                                : 'Enter a number, e.g. 1234.567'
                            }
                          >
                            <Input
                              mono
                              inputMode="decimal"
                              aria-label={`${side} ${name} for pair ${pair.label}`}
                              value={pair[field]}
                              onChange={(event) =>
                                onUpdatePair(pair.id, field, event.target.value)
                              }
                            />
                          </Field>
                        );
                      })}
                    </div>
                    {pair[description] && (
                      <p className="text-xs text-fg-muted">
                        {pair[description]}
                      </p>
                    )}
                  </fieldset>
                );
              }
            )}
          </div>
          {fits.length > 0 && (
            <section aria-label="Pair residuals" className="space-y-2">
              <h3 className="text-sm font-semibold text-fg">
                Residuals
                {units && (
                  <span className="ml-1.5 font-normal text-fg-muted">
                    ({units})
                  </span>
                )}
              </h3>
              {solutionStale && (
                <Alert tone="warning">
                  The accepted fit reflects previous inputs. Solve again to
                  update it.
                </Alert>
              )}
              <div className="overflow-hidden rounded-lg border border-border">
                <Table aria-label="Residual components">
                  <THead>
                    <Tr>
                      <Th>Fit</Th>
                      <Th numeric>North</Th>
                      <Th numeric>East</Th>
                      <Th numeric>Distance</Th>
                      <Th numeric>Azimuth</Th>
                    </Tr>
                  </THead>
                  <TBody className="[&>tr:last-child>th]:border-b-0">
                    {fits.map(({ name, fit }) => {
                      const residual = hasResidual(pair.weight)
                        ? fit.residuals.find((value) => value.id === pair.id)
                        : undefined;
                      return (
                        <Tr key={name}>
                          <Th
                            scope="row"
                            className="h-10 bg-transparent text-sm font-normal normal-case tracking-normal text-fg"
                          >
                            {name}
                          </Th>
                          <Td numeric>
                            {formatFixed(residual?.vn, 3, { signed: true })}
                          </Td>
                          <Td numeric>
                            {formatFixed(residual?.ve, 3, { signed: true })}
                          </Td>
                          <Td numeric>{formatFixed(residual?.total, 3)}</Td>
                          <Td numeric>{formatAzimuth(residual?.azimuth)}</Td>
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              </div>
            </section>
          )}
        </DialogBody>
        <DialogFooter className="sm:justify-between">
          <Button
            leadingIcon={<Trash2 />}
            onClick={() => {
              onRemovePair(pair.id);
              onClose();
            }}
          >
            Remove pair
          </Button>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
